import { createJSONStorage, type PersistStorage } from 'zustand/middleware';

/**
 * Isolation du localStorage par utilisateur pour les stores Zustand.
 *
 * Problème adressé (CW-21966 et plus large) : sans isolation, un user A
 * laissait ses filtres / drafts dans le localStorage, et un user B qui se
 * connectait sur le même navigateur héritait silencieusement de ces données
 * (filtres appliqués sans qu'il puisse les voir, drafts saisis par un autre,
 * etc.). Sur device partagé (kiosque OnTime, poste de bureau, démo), c'est
 * une fuite UX + données.
 *
 * Mécanisme : les clés exposées via `userScopedStorage` sont préfixées par
 * l'`userId` courant — `event-filters:333200000023`. Le flux d'authentification
 * appelle `setUserScope` au login et `clearUserScope` au logout.
 *
 * Hors périmètre (à laisser tel quel) : `apiCode`, `apiUrl`, `sockets`,
 * `accessToken`, etc. — ces clés sont posées par `Api.js` directement sur
 * localStorage et représentent des données device-scoped (pairing, session)
 * qui doivent survivre aux changements de user.
 */

let currentUserId: string | null = null;

export const setUserScope = (userId: string | number | null) => {
  currentUserId = userId != null ? String(userId) : null;
};

export const clearUserScope = () => {
  currentUserId = null;
};

export const getUserScope = (): string | null => currentUserId;

/**
 * Storage adapter à passer à l'option `storage` de `persist`.
 *
 * - Tant que `setUserScope` n'a pas été appelé, `getItem` retourne null →
 *   le store reste sur ses valeurs par défaut. Combiner avec `skipHydration: true`
 *   et appeler manuellement `store.persist.rehydrate()` après `setUserScope`.
 * - Au logout, `clearUserScope()` + remise à zéro du state via `setState` côté store.
 *   Les clés de l'ancien user restent en localStorage mais ne sont plus lues
 *   tant qu'aucun user n'est connecté.
 */
export const userScopedStorage = <T>(): PersistStorage<T> | undefined =>
  createJSONStorage<T>(() => ({
    getItem: (name) => {
      if (!currentUserId) return null;
      return localStorage.getItem(`${name}:${currentUserId}`);
    },
    setItem: (name, value) => {
      if (!currentUserId) return;
      localStorage.setItem(`${name}:${currentUserId}`, value);
    },
    removeItem: (name) => {
      if (!currentUserId) return;
      localStorage.removeItem(`${name}:${currentUserId}`);
    }
  }));
